import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import ScrollToTop from "./components/ScrollToTop";
import { getErrorReporter } from "./lib/errorReporter";

// Public pages - loaded immediately
import Home from "./pages/Home";
import Services from "./pages/Services";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import NewClient from "./pages/NewClient";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import LinkPartners from "./pages/LinkPartners";
import ScheduleAppointment from "./pages/ScheduleAppointment";
import NotFound from "./pages/NotFound";

// Auth pages - loaded immediately
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import ResetPassword from "./pages/auth/ResetPassword";
import AdminLogin from "./pages/admin/Login";

// Protected routes
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminRoute from "./components/auth/AdminRoute";

// Client portal pages - lazy loaded
const ClientDashboard = lazy(() => import("./pages/client-portal/Dashboard"));
const ClientAppointments = lazy(() => import("./pages/client-portal/Appointments"));
const ClientDocuments = lazy(() => import("./pages/client-portal/Documents"));
const ClientProfile = lazy(() => import("./pages/client-portal/Profile"));
const ClientEmailHistory = lazy(() => import("./pages/client-portal/EmailHistory"));

// Admin pages - lazy loaded for smaller initial bundle
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminClients = lazy(() => import("./pages/admin/Clients"));
const AdminChat = lazy(() => import("./pages/admin/Chat"));
const AdminDocuments = lazy(() => import("./pages/admin/Documents"));
const AdminScheduling = lazy(() => import("./pages/admin/Scheduling"));
const AdminSchedulingCalendar = lazy(() => import("./pages/admin/SchedulingCalendar"));
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminIntakeForms = lazy(() => import("./pages/admin/IntakeForms"));
const AdminReviews = lazy(() => import("./pages/admin/Reviews"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminAvailability = lazy(() => import("./pages/admin/Availability"));
const AdminEmail = lazy(() => import("./pages/admin/Email"));

// Loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Component to track route changes
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    try {
      const reporter = getErrorReporter();
      reporter.addBreadcrumb({
        category: 'navigation',
        message: `Navigated to ${location.pathname}`,
        data: { 
          pathname: location.pathname,
          search: location.search,
        },
      });
    } catch (err) {
      // Reporter not initialized yet, ignore
    }
  }, [location]);
  
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouteTracker />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public pages */}
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/new-client" element={<NewClient />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/link-partners" element={<LinkPartners />} />
            <Route path="/schedule-appointment" element={<ScheduleAppointment />} />
            
            {/* Auth pages */}
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/signup" element={<Signup />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Client portal - protected & lazy loaded */}
            <Route path="/client-portal" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
            <Route path="/client-portal/profile" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
            <Route path="/client-portal/appointments" element={<ProtectedRoute><ClientAppointments /></ProtectedRoute>} />
            <Route path="/client-portal/documents" element={<ProtectedRoute><ClientDocuments /></ProtectedRoute>} />
            <Route path="/client-portal/email-history" element={<ProtectedRoute><ClientEmailHistory /></ProtectedRoute>} />
            
            {/* Admin pages - protected & lazy loaded */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/clients" element={<AdminRoute><AdminClients /></AdminRoute>} />
            <Route path="/admin/chat" element={<AdminRoute><AdminChat /></AdminRoute>} />
            <Route path="/admin/documents" element={<AdminRoute><AdminDocuments /></AdminRoute>} />
            <Route path="/admin/scheduling" element={<AdminRoute><AdminScheduling /></AdminRoute>} />
            <Route path="/admin/scheduling/calendar" element={<AdminRoute><AdminSchedulingCalendar /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
            <Route path="/admin/availability" element={<AdminRoute><AdminAvailability /></AdminRoute>} />
            <Route path="/admin/email" element={<AdminRoute><AdminEmail /></AdminRoute>} />
            <Route path="/admin/intake" element={<AdminRoute><AdminIntakeForms /></AdminRoute>} />
            <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
            <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
